import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { SKIP_LOADING } from '@ribera-web/auth-data-access';
import {
  Cliente,
  ClienteFormValue,
  ConsultaClientes,
  CuentaInclub,
  PaginaClientes,
  ResultadoImportacion,
  TipoCliente,
} from '../models/clientes.model';
import { CLIENTES_API_URL } from './clientes-api.token';
import { ApiEnvelope, CustomerDto, ImportResultDto, MemberLookupDto, PageDto } from './clientes-api.types';
import {
  clienteDesdeApi,
  clienteHaciaApi,
  cuentaInclubDesdeApi,
  filtroSocios,
  importacionDesdeApi,
  paginaDesdeApi,
  socioHaciaApi,
  tipoApi,
} from './clientes.mapper';

@Injectable({ providedIn: 'root' })
export class ClientesApiService {
  private readonly http = inject(HttpClient);
  private readonly base = inject(CLIENTES_API_URL);

  buscar(consulta: ConsultaClientes): Observable<PaginaClientes> {
    let params = new HttpParams()
      .set('type', tipoApi(consulta.tipo))
      .set('page', consulta.pagina)
      .set('size', consulta.tamano);
    const socios = filtroSocios(consulta.tipo);
    if (socios !== null) {
      params = params.set('member', socios);
    }
    if (consulta.estado) {
      params = params.set('active', consulta.estado === 'activo');
    }
    if (consulta.busqueda.trim()) {
      params = params.set('search', consulta.busqueda.trim());
    }
    const context = new HttpContext().set(SKIP_LOADING, true);
    return this.http
      .get<ApiEnvelope<PageDto<CustomerDto>>>(this.base, { params, context })
      .pipe(map((respuesta) => paginaDesdeApi(respuesta.data)));
  }

  crear(form: ClienteFormValue): Observable<Cliente> {
    return this.http
      .post<ApiEnvelope<CustomerDto>>(this.base, clienteHaciaApi(form, true))
      .pipe(map((respuesta) => clienteDesdeApi(respuesta.data)));
  }

  actualizar(id: string, form: ClienteFormValue): Observable<Cliente> {
    return this.http
      .put<ApiEnvelope<CustomerDto>>(`${this.base}/${id}`, clienteHaciaApi(form, false))
      .pipe(map((respuesta) => clienteDesdeApi(respuesta.data)));
  }

  buscarSocio(numeroDocumento: string): Observable<CuentaInclub> {
    const params = new HttpParams().set('document_number', numeroDocumento);
    const context = new HttpContext().set(SKIP_LOADING, true);
    return this.http
      .get<ApiEnvelope<MemberLookupDto>>(`${this.base}/members/lookup`, { params, context })
      .pipe(map((respuesta) => cuentaInclubDesdeApi(respuesta.data)));
  }

  crearSocio(form: ClienteFormValue): Observable<Cliente> {
    return this.http
      .post<ApiEnvelope<CustomerDto>>(`${this.base}/members`, socioHaciaApi(form))
      .pipe(map((respuesta) => clienteDesdeApi(respuesta.data)));
  }

  sincronizarSocio(id: string): Observable<Cliente> {
    const context = new HttpContext().set(SKIP_LOADING, true);
    return this.http
      .post<ApiEnvelope<CustomerDto>>(`${this.base}/${id}/membership/sync`, null, { context })
      .pipe(map((respuesta) => clienteDesdeApi(respuesta.data)));
  }

  descargarPlantilla(tipo: TipoCliente, incluirClientes: boolean): Observable<Blob> {
    const params = new HttpParams().set('type', tipoApi(tipo)).set('include_customers', incluirClientes);
    return this.http.get(`${this.base}/template`, { params, responseType: 'blob' });
  }

  importar(tipo: TipoCliente, archivo: File): Observable<ResultadoImportacion> {
    const cuerpo = new FormData();
    cuerpo.append('file', archivo, archivo.name);
    return this.http
      .post<ApiEnvelope<ImportResultDto>>(`${this.base}/import`, cuerpo, {
        params: new HttpParams().set('type', tipoApi(tipo)),
      })
      .pipe(map((respuesta) => importacionDesdeApi(respuesta.data)));
  }
}
